// Matrix — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/mathBit.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer using the multi-row array convention for grids.

const matrixProblems = [
  {
    id: 'set-matrix-zeroes',
    title: 'Set Matrix Zeroes',
    difficulty: 'Medium',
    leetcode: { number: 73, url: 'https://leetcode.com/problems/set-matrix-zeroes/' },
    statement: 'Given an m×n matrix, if an element is 0, set its entire row and column to 0. Do it in place.',
    example: {
      input: 'matrix = [[1,1,1],[1,0,1],[1,1,1]]',
      output: '[[1,0,1],[0,0,0],[1,0,1]]',
      note: 'The single 0 at [1][1] zeroes out all of row 1 and all of column 1.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Track Zero Positions Separately)',
        why:
          "Make a first pass to record which rows and columns contain a 0, storing that in separate boolean " +
          "sets. Then a second pass zeroes out any cell whose row or column was marked. Correct, and easy to " +
          "reason about, but uses O(m + n) extra space for the tracking sets.",
        steps: [
          'Scan the whole matrix once, recording every row index and column index that contains at least one 0.',
          'Scan the matrix again: for each cell, if its row or column was marked, set it to 0.',
          'Two full passes total, plus O(m + n) auxiliary storage.',
        ],
        code:
`void setZeroesBruteForce(int[][] matrix) {
    int rows = matrix.length, cols = matrix[0].length;
    Set<Integer> zeroRows = new HashSet<>();
    Set<Integer> zeroCols = new HashSet<>();
    for (int i = 0; i < rows; i++) {
        for (int j = 0; j < cols; j++) {
            if (matrix[i][j] == 0) { zeroRows.add(i); zeroCols.add(j); }
        }
    }
    for (int i = 0; i < rows; i++) {
        for (int j = 0; j < cols; j++) {
            if (zeroRows.contains(i) || zeroCols.contains(j)) matrix[i][j] = 0;
        }
    }
}`,
        complexity: { time: 'O(m · n)', space: 'O(m + n) for the tracking sets' },
        whenToUse: "Perfectly fine, and clear to explain — but the O(1)-space version below is the expected follow-up in most interviews.",
      },
      {
        name: 'Approach 2 — Optimized: Use the Matrix\'s Own First Row/Column as Markers',
        why:
          "Instead of separate tracking sets, reuse the matrix's OWN first row and first column as the marker " +
          "storage — matrix[i][0] and matrix[0][j] can record \"row i (or column j) needs zeroing\" without any " +
          "extra memory. Two boolean flags handle the first row/column's own original state separately.",
        steps: [
          'First, check (and remember) whether the first row or first column originally contained a 0.',
          'Scan the rest of the matrix (rows/cols 1+): whenever a 0 is found at [i][j], mark matrix[i][0] = 0 and matrix[0][j] = 0.',
          'Scan again: for every cell [i][j] (rows/cols 1+), zero it if its marker matrix[i][0] or matrix[0][j] is 0.',
          'Finally, zero the first row and/or first column themselves, based on the flags recorded in step 1.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 1, 1] },
            { label: 'row 1', values: [1, 0, 1] },
            { label: 'row 2', values: [1, 1, 1] },
          ],
          varNames: [],
          steps: [
            { note: 'First row = [1,1,1] (no 0) and first column = [1,1,1] (no 0) — neither needs special handling at the end.' },
            { snapshots: { 'row 0': [1, 0, 1], 'row 1': [0, 0, 1] }, note: 'Found matrix[1][1] = 0 — mark matrix[1][0] = 0 and matrix[0][1] = 0 as markers.' },
            { snapshots: { 'row 0': [1, 0, 1], 'row 1': [0, 0, 0], 'row 2': [1, 0, 1] }, note: 'Second pass: any cell whose row-marker or column-marker is 0 gets zeroed — [1][2] (row marker 0), [2][1] (col marker 0).' },
            { note: 'Neither the first row nor first column originally had a 0, so no further changes. Final: [[1,0,1],[0,0,0],[1,0,1]].' },
          ],
        },
        code:
`void setZeroes(int[][] matrix) {
    int rows = matrix.length, cols = matrix[0].length;
    boolean firstRowZero = false, firstColZero = false;
    for (int j = 0; j < cols; j++) if (matrix[0][j] == 0) firstRowZero = true;
    for (int i = 0; i < rows; i++) if (matrix[i][0] == 0) firstColZero = true;

    for (int i = 1; i < rows; i++) {
        for (int j = 1; j < cols; j++) {
            if (matrix[i][j] == 0) { matrix[i][0] = 0; matrix[0][j] = 0; }
        }
    }
    for (int i = 1; i < rows; i++) {
        for (int j = 1; j < cols; j++) {
            if (matrix[i][0] == 0 || matrix[0][j] == 0) matrix[i][j] = 0;
        }
    }
    if (firstRowZero) for (int j = 0; j < cols; j++) matrix[0][j] = 0;
    if (firstColZero) for (int i = 0; i < rows; i++) matrix[i][0] = 0;
}`,
        complexity: { time: 'O(m · n)', space: 'O(1) extra' },
        interviewNote: "Try the \"Set Matrix Zeroes\" mode in this pattern's interactive playground to watch the marker-based passes run on any grid.",
        whenToUse: "The expected optimal solution whenever an in-place, O(1)-space matrix mutation is asked for — reusing part of the input's own storage as scratch space is a recurring trick worth recognizing.",
      },
    ],
  },
  {
    id: 'spiral-matrix',
    title: 'Spiral Matrix',
    difficulty: 'Medium',
    leetcode: { number: 54, url: 'https://leetcode.com/problems/spiral-matrix/' },
    statement: 'Given an m×n matrix, return all its elements in spiral order (starting from the top-left, going right, then down, then left, then up, and repeating inward).',
    example: {
      input: 'matrix = [[1,2,3],[4,5,6],[7,8,9]]',
      output: '[1,2,3,6,9,8,7,4,5]',
      note: 'Trace the outer ring clockwise, then the single remaining center element.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Simulate With a Visited Grid)',
        why:
          "Walk the matrix one step at a time, always trying to continue in the current direction, and turning " +
          "clockwise the moment the next cell would be out of bounds or already visited. Correct and intuitive, " +
          "but requires a full auxiliary visited-tracking grid.",
        steps: [
          'Track the current position, current direction, and a visited grid the same size as the input.',
          'At each step, record the current cell and mark it visited.',
          'If the next cell in the current direction is out of bounds or already visited, turn 90° clockwise.',
          'Continue until every cell has been visited.',
        ],
        code:
`List<Integer> spiralOrderBruteForce(int[][] matrix) {
    int m = matrix.length, n = matrix[0].length;
    boolean[][] visited = new boolean[m][n];
    int[][] dirs = { {0,1}, {1,0}, {0,-1}, {-1,0} };   // right, down, left, up
    List<Integer> result = new ArrayList<>();
    int row = 0, col = 0, dir = 0;
    for (int i = 0; i < m * n; i++) {
        result.add(matrix[row][col]);
        visited[row][col] = true;
        int nr = row + dirs[dir][0], nc = col + dirs[dir][1];
        if (nr < 0 || nr >= m || nc < 0 || nc >= n || visited[nr][nc]) {
            dir = (dir + 1) % 4;
            nr = row + dirs[dir][0]; nc = col + dirs[dir][1];
        }
        row = nr; col = nc;
    }
    return result;
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) for the visited grid' },
        whenToUse: "A very natural first approach — but the boundary-shrinking version below achieves the same result without any extra grid at all.",
      },
      {
        name: 'Approach 2 — Optimized: Shrinking Boundaries',
        why:
          "Rather than checking \"has this cell been visited\" at every step, track four boundaries — top, " +
          "bottom, left, right — that shrink inward by exactly one after each full sweep of a side. A cell is " +
          "never re-visited because the boundaries themselves guarantee it's already been passed.",
        steps: [
          'Sweep the top row left to right, then move the top boundary down by one.',
          'Sweep the right column top to bottom, then move the right boundary left by one.',
          'If rows remain, sweep the bottom row right to left, then move the bottom boundary up by one.',
          'If columns remain, sweep the left column bottom to top, then move the left boundary right by one. Repeat until the boundaries cross.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 2, 3] },
            { label: 'row 1', values: [4, 5, 6] },
            { label: 'row 2', values: [7, 8, 9] },
          ],
          varNames: [],
          steps: [
            { highlights: { 'row 0': [0, 1, 2] }, note: 'Top row, left→right: 1, 2, 3. top moves to row 1.' },
            { highlights: { 'row 1': [2], 'row 2': [2] }, note: 'Right column, top→bottom: 6, 9. right moves to column 1.' },
            { highlights: { 'row 2': [1, 0] }, note: 'Bottom row, right→left: 8, 7. bottom moves to row 1.' },
            { highlights: { 'row 1': [0] }, note: 'Left column, bottom→top: 4. left moves to column 1. Now top=bottom=left=right=1 — only the center cell (5) remains, read on the next sweep.' },
            { note: 'Final spiral order: [1, 2, 3, 6, 9, 8, 7, 4, 5].' },
          ],
        },
        code:
`List<Integer> spiralOrder(int[][] matrix) {
    int top = 0, bottom = matrix.length - 1;
    int left = 0, right = matrix[0].length - 1;
    List<Integer> result = new ArrayList<>();
    while (top <= bottom && left <= right) {
        for (int j = left; j <= right; j++) result.add(matrix[top][j]);
        top++;
        for (int i = top; i <= bottom; i++) result.add(matrix[i][right]);
        right--;
        if (top <= bottom) {
            for (int j = right; j >= left; j--) result.add(matrix[bottom][j]);
            bottom--;
        }
        if (left <= right) {
            for (int i = bottom; i >= top; i--) result.add(matrix[i][left]);
            left++;
        }
    }
    return result;
}`,
        complexity: { time: 'O(m · n)', space: 'O(1) extra beyond the output' },
        interviewNote: "Try the \"Spiral Matrix\" mode in this pattern's interactive playground to watch the boundaries shrink on any grid up to 6×6.",
        whenToUse: "The standard, expected solution — the four-boundary bookkeeping is the single most reusable idea in this entire pattern, directly powering Spiral Matrix II as well.",
      },
      {
        name: 'Approach 3 — Alternative: Recursive Layer Peeling',
        why:
          "A spiral is really just a sequence of concentric rectangular RINGS, each one layer further inside " +
          "the last. Reading the outer ring, then recursively reading the spiral of the smaller matrix left " +
          "behind inside it, is a completely different (but equally valid) way to arrive at the same order.",
        steps: [
          'Base case: an empty remaining sub-matrix contributes nothing.',
          'Read the outermost ring of the current sub-matrix: top row left-to-right, right column top-to-bottom (excluding the corner already read), bottom row right-to-left, left column bottom-to-top (excluding both corners already read).',
          'Recurse on the sub-matrix strictly inside that ring (excluding the first/last row and column just read).',
          'Concatenate the current ring\'s reading with the result of the recursive call.',
        ],
        code:
`List<Integer> spiralOrderRecursive(int[][] matrix) {
    List<Integer> result = new ArrayList<>();
    peelLayer(matrix, 0, matrix.length - 1, 0, matrix[0].length - 1, result);
    return result;
}

private void peelLayer(int[][] matrix, int top, int bottom, int left, int right, List<Integer> result) {
    if (top > bottom || left > right) return;
    for (int j = left; j <= right; j++) result.add(matrix[top][j]);
    for (int i = top + 1; i <= bottom; i++) result.add(matrix[i][right]);
    if (top < bottom) {
        for (int j = right - 1; j >= left; j--) result.add(matrix[bottom][j]);
    }
    if (left < right) {
        for (int i = bottom - 1; i > top; i--) result.add(matrix[i][left]);
    }
    peelLayer(matrix, top + 1, bottom - 1, left + 1, right - 1, result);
}`,
        complexity: { time: 'O(m · n)', space: 'O(min(m, n)) — recursion depth, one call per ring' },
        whenToUse: "A nice alternative to demonstrate divide-and-conquer thinking on the same problem — functionally equivalent to the iterative boundary-shrinking version, just expressed as \"peel a ring, recurse inward\" instead of a single loop.",
      },
    ],
  },
  {
    id: 'rotate-image',
    title: 'Rotate Image',
    difficulty: 'Medium',
    leetcode: { number: 48, url: 'https://leetcode.com/problems/rotate-image/' },
    statement: 'Given an n×n matrix representing an image, rotate it 90 degrees clockwise, in place.',
    example: {
      input: 'matrix = [[1,2,3],[4,5,6],[7,8,9]]',
      output: '[[7,4,1],[8,5,2],[9,6,3]]',
      note: 'Every column, read bottom-to-top, becomes a row.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Build a New Rotated Matrix)',
        why:
          "Directly apply the rotation formula — the element at [i][j] moves to [j][n-1-i] — into a brand new " +
          "matrix, then copy it back over the original. Correct and easy to derive from the rotation geometry " +
          "directly, but doesn't satisfy a strict in-place requirement on its own.",
        steps: [
          'Allocate a new n×n matrix.',
          'For every cell [i][j] in the original, place its value at [j][n-1-i] in the new matrix.',
          'Copy the new matrix\'s contents back into the original matrix\'s cells.',
        ],
        code:
`void rotateBruteForce(int[][] matrix) {
    int n = matrix.length;
    int[][] rotated = new int[n][n];
    for (int i = 0; i < n; i++) {
        for (int j = 0; j < n; j++) {
            rotated[j][n - 1 - i] = matrix[i][j];
        }
    }
    for (int i = 0; i < n; i++) {
        matrix[i] = rotated[i];
    }
}`,
        complexity: { time: 'O(n²)', space: 'O(n²) for the new matrix' },
        whenToUse: "Useful to confirm the rotation FORMULA directly — but the transpose-then-reverse approach below achieves the identical result using none of this extra memory.",
      },
      {
        name: 'Approach 2 — Optimized: Transpose, Then Reverse Each Row',
        why:
          "A 90° clockwise rotation can be decomposed into two simpler, well-known in-place operations: first " +
          "TRANSPOSE the matrix (flip across the main diagonal, swapping matrix[i][j] with matrix[j][i]), then " +
          "REVERSE each row. Both steps are O(1)-extra-space operations, and their composition is exactly a " +
          "90° clockwise rotation.",
        steps: [
          'Transpose the matrix in place: for each cell above the diagonal, swap it with its mirror below the diagonal.',
          'After transposing, every column of the original is now a row.',
          'Reverse each row of the transposed matrix.',
          'The combination of transpose + row-reversal is exactly a 90° clockwise rotation.',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 2, 3] },
            { label: 'row 1', values: [4, 5, 6] },
            { label: 'row 2', values: [7, 8, 9] },
          ],
          varNames: [],
          steps: [
            { snapshots: { 'row 0': [1, 4, 3], 'row 1': [2, 5, 6] }, note: 'Transpose: swap [0][1]=2 and [1][0]=4.' },
            { snapshots: { 'row 0': [1, 4, 7], 'row 2': [3, 8, 9] }, note: 'Transpose: swap [0][2]=3 and [2][0]=7.' },
            { snapshots: { 'row 1': [2, 5, 8], 'row 2': [3, 6, 9] }, note: 'Transpose: swap [1][2]=6 and [2][1]=8. Transpose complete: [[1,4,7],[2,5,8],[3,6,9]].' },
            { snapshots: { 'row 0': [7, 4, 1] }, note: 'Reverse row 0: [1,4,7] → [7,4,1].' },
            { snapshots: { 'row 1': [8, 5, 2] }, note: 'Reverse row 1: [2,5,8] → [8,5,2].' },
            { snapshots: { 'row 2': [9, 6, 3] }, note: 'Reverse row 2: [3,6,9] → [9,6,3]. Final: [[7,4,1],[8,5,2],[9,6,3]] — the matrix rotated 90° clockwise, in place.' },
          ],
        },
        code:
`void rotate(int[][] matrix) {
    int n = matrix.length;
    for (int i = 0; i < n; i++) {
        for (int j = i + 1; j < n; j++) {
            int temp = matrix[i][j];
            matrix[i][j] = matrix[j][i];
            matrix[j][i] = temp;
        }
    }
    for (int[] row : matrix) {
        reverse(row);
    }
}

private void reverse(int[] row) {
    int lo = 0, hi = row.length - 1;
    while (lo < hi) {
        int temp = row[lo]; row[lo] = row[hi]; row[hi] = temp;
        lo++; hi--;
    }
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
        interviewNote: "Try the \"Rotate Image\" mode in this pattern's interactive playground to watch the transpose and row-reversal phases run separately on any square grid.",
        whenToUse: "The standard, expected solution for any true in-place matrix rotation — decomposing a geometric transformation into two simpler, well-known steps (transpose, reverse) is a reusable idea beyond just this problem.",
      },
    ],
  },
];

export default matrixProblems;
